import API from './apiBase';

const pct = (n, digits = 2) => {
  const num = Number(n ?? 0);
  if (!isFinite(num)) return `0.00%`;
  const asPct = num <= 1 ? num * 100 : num;
  return `${asPct.toFixed(digits)}%`;
};

function normalizePct(val) {
  if (val == null) return 0;
  const n = Number(val);
  if (Number.isNaN(n)) return 0;
  return n > 1 ? n / 100 : n;
}

function num(v) {
  if (v == null) return 0;
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  const s = String(v).trim();
  if (!s) return 0;
  const cleaned = s.replace(/[^0-9.+\-Ee]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

async function fetchJson(url, options) {
  const res = await fetch(url, options);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function resolveLatestCalcId(tenantId, tbId, tries = 5, delayMs = 1000) {
  for (let i = 0; i < tries; i++) {
    try {
      const params = new URLSearchParams({ tb_id: tbId });
      if (tenantId) params.set('tenant_id', tenantId);
      const latest = await fetchJson(`${API}/calculation/latest?${params.toString()}`);
      const cid = latest?.calculation_id;
      if (cid) return cid;
    } catch (_) {}
    await new Promise(r => setTimeout(r, delayMs));
  }
  return null;
}

function firstNumber(obj) {
  if (!obj || typeof obj !== 'object') return 0;
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    const n = num(v);
    if (n !== 0) return n;
  }
  return 0;
}

function getByVariants(row, variants, norm) {
  for (const k of variants) {
    if (row[k] != null) return row[k];
    const kn = k.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (norm[kn] != null) return norm[kn];
  }
  return undefined;
}

function normalizeRowKeys(r) {
  const norm = {};
  Object.keys(r || {}).forEach(k => {
    const kn = k.toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (norm[kn] == null) norm[kn] = r[k];
  });
  return norm;
}

function mapAccountRow(r) {
  const norm = normalizeRowKeys(r);
  const acct = getByVariants(r, ['ACCOUNT_NUMBER','account_number','Account #','Account','ACCOUNT','acct','gl account','gl code','accountno'], norm);
  const name = getByVariants(r, ['ACCOUNT_NAME','account_name','Account Name','name','NAME','description','desc'], norm);
  // Special handling for Schedule D backend rows (TOTAL_AMOUNT, GA_ALLOCATION, OH_ALLOCATION)
  const totalAmount = getByVariants(r, ['TOTAL_AMOUNT','total_amount'], norm);
  const gl = totalAmount != null ? totalAmount : getByVariants(r, ['GL_BALANCE','gl_balance','GL Balance','gl balance','balance','amount','AMOUNT','claimed_amount','CLAIMED_AMOUNT','net_amount','NET_AMOUNT'], norm);
  const adj = getByVariants(r, ['ADJUSTMENTS','adjustments','adj','ADJ'], norm);
  const claimed = totalAmount != null ? totalAmount : getByVariants(r, ['CLAIMED_AMOUNT','claimed_amount','claimed','amount','net_amount','NET_AMOUNT'], norm);
  return {
    ACCOUNT_NUMBER: acct ?? '',
    ACCOUNT_NAME: name ?? '',
    GL_BALANCE: num(gl ?? 0),
    ADJUSTMENTS: num(adj ?? 0),
    CLAIMED_AMOUNT: num(claimed ?? gl ?? 0),
  };
}

function adaptSchedules(raw, pools, calcDoc) {
  const result = { A: null, B: { title: undefined, rows: [] }, C: { title: undefined, rows: [] }, D: { title: undefined }, E: null };

  // Schedule A
  const A = raw?.A;
  let aRows = [];
  if (A) {
    const data = Array.isArray(A.data) ? A.data : (Array.isArray(A.rows) ? A.rows : []);
    if (data.length) {
      aRows = data.map(r => ({
        pool: r.pool || r.POOL || r.name || r.Name || r.label || '',
        amount: num(r.amount ?? r.AMOUNT ?? r.value ?? r.total ?? firstNumber(r))
      }));
    }
  }
  const hasNames = aRows.some(r => r.pool && String(r.pool).trim() !== '');
  if (!hasNames) {
    const candidates = [
      ['G&A Pool', pools['G&A'] ?? pools['GA'] ?? pools['Gna']],
      ['Overhead Pool', pools['OH'] ?? pools['Overhead']],
      ['Fringe Pool', pools['Fringe']]
    ];
    aRows = candidates.filter(([_, v]) => typeof v === 'number' && isFinite(v)).map(([label, v]) => ({ pool: label, amount: num(v) }));
  }
  result.A = { title: (A && (A.schedule_name || A.title)) || 'Summary of Claimed Indirect Rates', rows: aRows };

  // Schedule B
  const B = raw?.B;
  if (B) {
    const bdata = Array.isArray(B.rows) ? B.rows : (Array.isArray(B.data) ? B.data : []);
    result.B = { title: B.schedule_name || B.title || 'G&A Expenses', rows: bdata.map(mapAccountRow) };
  } else {
    result.B = { title: 'G&A Expenses', rows: [] };
  }

  // Schedule C
  const C = raw?.C;
  if (C) {
    const cdata = Array.isArray(C.rows) ? C.rows : (Array.isArray(C.data) ? C.data : []);
    result.C = { title: C.schedule_name || C.title || 'Overhead Expenses', rows: cdata.map(mapAccountRow) };
  } else {
    result.C = { title: 'Overhead Expenses', rows: [] };
  }

  // Schedule D
  const D = raw?.D || {};
  let drows = [];
  const ddata = Array.isArray(D.rows) ? D.rows : (Array.isArray(D.data) ? D.data : []);
  if (ddata.length) {
    drows = ddata.map(mapAccountRow);
  }
  // If D rows missing or sum to zero, attempt to derive from calcDoc.allocations/intermediate + calcDoc.accounts
  const drowsTotal = (drows || []).reduce((acc, r) => acc + num(r.CLAIMED_AMOUNT ?? r.GL_BALANCE ?? 0), 0);
  if ((!drows || drows.length === 0 || drowsTotal === 0) && Array.isArray(calcDoc?.accounts)) {
    const interAccounts = calcDoc.accounts.filter(a => String(a.pool || '').toLowerCase().includes('intermediate'));
    drows = interAccounts.map(a => mapAccountRow({ account_number: a.account_number || a.account, account_name: a.account_name, net_amount: a.net_amount ?? a.amount ?? a.balance }));
  }
  // Compute summary robustly
  let interTotal = num((pools && (pools['Intermediate'] || pools['INTERMEDIATE'])) || D.total || D.intermediate_total || 0);
  if ((!interTotal || interTotal === 0) && drows.length) {
    interTotal = drows.reduce((acc, r) => acc + num(r.CLAIMED_AMOUNT ?? r.GL_BALANCE ?? 0), 0);
  }
  let gaPct = normalizePct(D.ga_pct ?? D.GA_PCT ?? D.gaPercent);
  let ohPct = normalizePct(D.oh_pct ?? D.OH_PCT ?? D.ohPercent);
  let toGA = num(D.to_ga_amount ?? D.ga_amount ?? D.GA_AMOUNT);
  let toOH = num(D.to_oh_amount ?? D.oh_amount ?? D.OH_AMOUNT);
  // Fallback to authoritative calc.allocations if schedule D does not include summary fields
  const allocInter = (calcDoc?.allocations && calcDoc.allocations.intermediate) ? calcDoc.allocations.intermediate : null;
  if (allocInter) {
    if (!gaPct || !ohPct) {
      const ag = normalizePct(allocInter.ga_pct);
      const oh = normalizePct(allocInter.oh_pct);
      if (ag && oh) { gaPct = ag; ohPct = oh; }
    }
    if (!toGA || !toOH) {
      const agAmt = num(allocInter.GA ?? allocInter.ga ?? 0);
      const ohAmt = num(allocInter.OH ?? allocInter.oh ?? 0);
      if (!toGA && agAmt) toGA = agAmt;
      if (!toOH && ohAmt) toOH = ohAmt;
    }
  }
  if ((!toGA || toGA === 0) && interTotal && gaPct) toGA = interTotal * gaPct;
  if ((!toOH || toOH === 0) && interTotal && ohPct) toOH = interTotal * ohPct;
  const inter = { ga_pct: gaPct, oh_pct: ohPct, to_ga_amount: toGA, to_oh_amount: toOH, intermediate_total: interTotal };
  result.D = { title: D.schedule_name || D.title || 'Intermediate Allocations', summary: inter, rows: drows };

  // Schedule E
  const E = raw?.E;
  let eRows = [];
  if (E) {
    const edata = Array.isArray(E.rows) ? E.rows : (Array.isArray(E.data) ? E.data : []);
    if (edata.length) {
      eRows = edata.map(r => ({
        base: r.base || r.base_name || r.BASE || r.name || r.label || '',
        amount: num(r.amount ?? r.AMOUNT ?? r.value ?? firstNumber(r))
      }));
    }
    if (!eRows.some(r => r.base && String(r.base).trim() !== '')) {
      const basesMap = calcDoc?.bases || {};
      eRows = Object.keys(basesMap).map(k => ({ base: k, amount: num(basesMap[k]) }));
    }
    result.E = { title: E.schedule_name || E.title || 'Allocation Bases', rows: eRows };
  } else {
    const basesMap = calcDoc?.bases || {};
    eRows = Object.keys(basesMap).map(k => ({ base: k, amount: num(basesMap[k]) }));
    result.E = { title: 'Allocation Bases', rows: eRows };
  }

  return result;
}

export async function getCurrentResultsByTbId(tenantId, tbId) {
  let calculationId = await resolveLatestCalcId(tenantId, tbId, 5, 1000);
  if (!calculationId) {
    return { calcId: null, tbId, rates: { oh: '0.00%', ga: '0.00%', fringe: '0.00%' }, pools: {}, bases: {}, schedules: { A: {}, B: { rows: [] }, C: { rows: [] }, D: {}, E: {} } };
  }
  const [calcDoc, schedulesResp] = await Promise.all([
    fetchJson(`${API}/calculation/${calculationId}`),
    fetchJson(`${API}/calculation/${calculationId}/schedules`).catch(() => ({})),
  ]);
  const schedulesRaw = schedulesResp?.schedules || schedulesResp || {};
  const rates = { oh: pct(calcDoc?.rates?.OH ?? calcDoc?.rates?.Oh ?? calcDoc?.rates?.oh), ga: pct(calcDoc?.rates?.['G&A'] ?? calcDoc?.rates?.GA ?? calcDoc?.rates?.ga), fringe: pct(calcDoc?.rates?.Fringe ?? calcDoc?.rates?.fringe) };
  const pools = calcDoc?.pools ?? {};
  const bases = calcDoc?.bases ?? {};
  const schedules = adaptSchedules(schedulesRaw, pools, calcDoc);
  return { calcId: calculationId, tbId: calcDoc?.tb_id ?? tbId, rates, pools, bases, schedules };
}

export async function applyIntermediateByTb(tbId, tenantId, gaPct, ohPct) {
  const payload = { ga_pct: gaPct, oh_pct: ohPct };
  if (tenantId) payload.tenant_id = tenantId;
  const res = await fetchJson(`${API}/calculation/${encodeURIComponent(tbId)}/apply-intermediate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  return res?.calculation_id;
}

export async function splitAccountByTb(tbId, tenantId, accountNumber, splits) {
  const payload = { account_number: accountNumber, splits };
  if (tenantId) payload.tenant_id = tenantId;
  const res = await fetchJson(`${API}/calculation/${encodeURIComponent(tbId)}/split-account`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  return res?.calculation_id;
}

export async function connectAIShim(calculationId) {
  return fetchJson(`${API}/ai/connect`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ calculation_id: calculationId }) });
}
